import { Link, createFileRoute } from '@tanstack/react-router';
import { Card } from '@/components/ui/card';
import { WikiAnchorSection } from '@/components/wiki/WikiAnchorSection';
import { WikiToc } from '@/components/wiki/WikiToc';
import { gameActions } from '@/lib/guide-structure';
import { GuideActionIcon } from '@/components/icons/GuideActionIcon';

export const Route = createFileRoute('/wiki/province-management')({
  component: ProvinceManagementHubPage,
});

function ProvinceManagementHubPage() {
  const toc = [
    { id: 'overview', label: 'Overview' },
    { id: 'core-pages', label: 'Core Pages' },
    { id: 'self-spell-tools', label: 'Self Spells' },
    { id: 'force-controls', label: 'Force Controls' },
  ];

  const selfSpellActionIds = [
    'builders_boon',
    'fertile_lands',
    'ghost_workers',
    'love_and_peace',
    'miners_mystique',
    'tree_of_gold',
  ];

  const forceControlActionIds = [
    'recycle_books',
    'set_military_options',
    'train_military',
    'release_troops',
  ];

  const selfSpellActions = selfSpellActionIds
    .map(actionId => gameActions.find(action => action.id === actionId))
    .filter((action): action is NonNullable<typeof action> => Boolean(action));

  const forceControlActions = forceControlActionIds
    .map(actionId => gameActions.find(action => action.id === actionId))
    .filter((action): action is NonNullable<typeof action> => Boolean(action));

  return (
    <div className="space-y-5">
      <WikiToc items={toc} />

      <WikiAnchorSection id="overview" title="Province Management Hub">
        <div className="grid gap-4 sm:grid-cols-[120px_1fr]">
          <img
            src="/images/nav/construction@3x.png"
            alt="Province management"
            className="w-full max-w-[120px] rounded-xl border border-[#d4af37]/25"
          />
          <p>
            This section focuses on economy cycles, build patterns, growth control, and province stability.
            It is designed as operational guidance: what to optimize, when to pivot, and what your kingdom
            role should prioritize.
          </p>
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="core-pages" title="Core Pages">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { to: '/economics', title: 'Economics' },
            { to: '/market', title: 'The Market' },
            { to: '/science', title: 'Science' },
            { to: '/trade', title: 'Trade & Aid' },
            { to: '/military', title: 'Military' },
            { to: '/wiki/reference', title: 'Age Bonuses' },
          ].map(page => (
            <Link key={page.to} to={page.to}>
              <Card className="p-4 transition-colors hover:border-[#d4af37]/40">
                <h3 className="font-cinzel text-lg text-[#f0f0f0]">{page.title}</h3>
              </Card>
            </Link>
          ))}
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="self-spell-tools" title="Province Self-Spells">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {selfSpellActions.map(action => (
            <Link key={action.id} to="/actions/$actionId" params={{ actionId: action.id }}>
              <Card className="p-3 transition-colors hover:border-[#d4af37]/40">
                <div className="flex items-center gap-3">
                  <GuideActionIcon
                    action={action}
                    width={48}
                    height={48}
                    className="h-12 w-12 overflow-hidden rounded-lg border border-[#d4af37]/25"
                    imgClassName="h-12 w-12 rounded-lg border border-[#d4af37]/25 object-cover"
                  />
                  <span className="font-semibold text-[#e6c547]">{action.title}</span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </WikiAnchorSection>

      <WikiAnchorSection id="force-controls" title="Recycle, Draft, Train, and Release">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {forceControlActions.map(action => (
            <Link key={action.id} to="/actions/$actionId" params={{ actionId: action.id }}>
              <Card className="p-3 transition-colors hover:border-[#d4af37]/40">
                <div className="flex items-center gap-3">
                  <GuideActionIcon
                    action={action}
                    width={48}
                    height={48}
                    className="h-12 w-12 overflow-hidden rounded-lg border border-[#d4af37]/25"
                    imgClassName="h-12 w-12 rounded-lg border border-[#d4af37]/25 object-cover"
                  />
                  <span className="font-semibold text-[#e6c547]">{action.title}</span>
                </div>
              </Card>
            </Link>
          ))}
        </div>
      </WikiAnchorSection>
    </div>
  );
}
